import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { loadIWSRecordsByPlacementId } from '../../../../../store/participantService';
import {
    countDays,
    isDateAfter,
    isDateEarlier,
    todayDate
} from '../../../../../utils/dateFunctions';
import { stableSort } from '../../../../../utils/sortFunctions';
import { hasRole } from '../../../../../utils/userRoles';
import Button from '../../../../formElements/Button';
import ResultsTable from '../../../../table/ResultsTable';
import LoadingSpinner from '../../../../ui/LoadingSpinner';

import AddCallEditorModal from './AddCallEditorModal';
import CreateInWorkSupportCall from './CreateInWorkSupportCall';
import InWorkSupportTableRow from './InWorkSupportTableRow';

const headCells = [
    { id: 'dueDate', label: 'Due Date' },
    { id: 'frequencyWeekNo', label: 'Frequency' },
    { id: 'callDate', label: 'Call Date/Time' },
    { id: 'workConfidenceName', label: 'Work Confidence' },
    { id: 'outcomeName', label: 'Outcome' }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'frequencyWeekNo',
    page: 0,
    rowsPerPage: 100
};

const InWorkSupportTableManagement = ({ acceptedRoles, currentCall, rows, placement }) => {
    // LOCAL STATE
    const [showForm, setShowForm] = useState(false);
    const [formType, setFormType] = useState('');
    const [openId, setOpenId] = useState('');
    const [isOpenAddCallEditor, setIsOpenAddCallEditor] = useState(false);
    const [addCallRow, setAddCallRow] = useState({});
    const [callAddedTodayAlready, setCallAddedTodayAlready] = useState(false);
    const [isWithinRequiredTime, setIsWithinRequiredTime] = useState(true);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const iwsRecordsByPlacementId = useSelector(
        (state) => state.entities.participantService.iwsRecordsByPlacementId
    );
    const loadingIwsRecordsByPlacementId = useSelector(
        (state) => state.entities.participantService.loadingIwsRecordsByPlacementId
    );
    const iwsRecordsMetaData = iwsRecordsByPlacementId[placement.id]?.metaData;
    const contractConstants = useSelector(
        (state) => state.entities.participantService.contractConstants
    );

    // USE EFFECTS
    useEffect(() => {
        const callToday = rows.some((el) => todayDate() === el.dueDate.substring(0, 10));
        if (callToday) setCallAddedTodayAlready(true);
    }, [rows]);

    useEffect(() => {
        if (Object.keys(contractConstants).length < 1) return;
        const isTodayAfterStartDate = isDateAfter(todayDate(), placement.startDate);
        const isTodayBeforeEndDate = placement.endDate
            ? isDateEarlier(placement.endDate, todayDate())
            : true;
        const numDays = contractConstants?.numDaysIWSRequired || 0;
        const isTodayBeforeNumOfDaysAfterStartDate =
            countDays(placement.startDate, todayDate()) < numDays;
        setIsWithinRequiredTime(
            isTodayAfterStartDate && isTodayBeforeEndDate && isTodayBeforeNumOfDaysAfterStartDate
        );
    }, [contractConstants]);

    // EVENT HANDLERS
    const onCreateInWorkSupportCall = () => {
        setFormType('create');
        setShowForm(true);
    };
    const onOpenAddCallEditor = (isOpen, row = {}) => {
        setAddCallRow(row);
        setIsOpenAddCallEditor(isOpen);
    };

    const onToggleDropdown = (id) => setOpenId(id);

    const createRows = () => {
        return stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <InWorkSupportTableRow
                key={el.id}
                row={el}
                toggleDropdown={onToggleDropdown}
                openId={openId}
                onOpenAddCallEditor={onOpenAddCallEditor}
            />
        ));
    };

    // RENDER
    return (
        <>
            {rows.length < 1 ? (
                <LoadingSpinner content="No IWS records found." />
            ) : (
                <ResultsTable
                    defaultOrderBy={rowMetaData.orderBy}
                    defaultRowsPerPage={rowMetaData.rowsPerPage}
                    headCells={headCells}
                    loadingValues={loadingIwsRecordsByPlacementId}
                    loadResults={loadIWSRecordsByPlacementId}
                    passRowMetaDataUp={setRowMetaData}
                    apiParams={placement.id}
                    tableRows={createRows()}
                    totalResults={iwsRecordsMetaData.totalElements}
                    metaData={iwsRecordsMetaData}
                />
            )}
            <div>
                {!showForm && (
                    <Button
                        id="addIwsCallButton"
                        testId="addIwsCallButton"
                        type="button"
                        content="Add In Work Support Call"
                        disabled={
                            !hasRole(acceptedRoles, roles) ||
                            !isWithinRequiredTime ||
                            !currentCall.frequencyId ||
                            callAddedTodayAlready
                        }
                        icon={true}
                        onClick={onCreateInWorkSupportCall}
                    />
                )}
                {showForm && formType === 'create' && (
                    <CreateInWorkSupportCall
                        acceptedRoles={acceptedRoles}
                        businessName={placement.businessName}
                        currentCall={currentCall}
                        onClose={() => setShowForm(false)}
                    />
                )}
            </div>
            <AddCallEditorModal
                acceptedRoles={acceptedRoles}
                isOpen={isOpenAddCallEditor}
                onOpenAddCallEditor={onOpenAddCallEditor}
                row={addCallRow}
                businessName={placement.businessName}
            />
        </>
    );
};
InWorkSupportTableManagement.propTypes = {
    acceptedRoles: PropTypes.arrayOf(PropTypes.string),
    currentCall: PropTypes.object,
    rows: PropTypes.arrayOf(PropTypes.object),
    placement: PropTypes.object
};

export default InWorkSupportTableManagement;
